import { useEffect, useState } from 'react';
import { Checkbox, Input, NumberInput } from '@/components/ui';
import { UpdatedSchoolParams } from '#server/models';
import { UCData } from './ModifySchoolParameters';
import { UC_ALLOCATION_METHOD } from '#server/constants/constants';

interface UCParamsProps {
  ucData: UCData;
  handleOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setUpdatedSchoolParams: React.Dispatch<React.SetStateAction<any>>;
  largeUnitsStudents: number;
}

export const UCParams = ({ ucData, handleOnChange, setUpdatedSchoolParams, largeUnitsStudents }: UCParamsProps) => {
  const [isRangeAllocation, setIsRangeAllocation] = useState<boolean>(false);
  const [isPlannedEnrolmentCalculation, setIsPlannedEnrolmentCalculation] = useState<boolean>(false);

  const handleOnCheckedChange = (checked: boolean, value: string) => {
    if (value === UC_ALLOCATION_METHOD.RANGE_ALLOCATION) {
      setIsRangeAllocation(checked);
      setIsPlannedEnrolmentCalculation(!checked);
      setUpdatedSchoolParams((prev: UpdatedSchoolParams) => ({
        ...prev,
        unitCoordinationAllocationMethod: checked
          ? UC_ALLOCATION_METHOD.RANGE_ALLOCATION
          : UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION,
      }));
    }
    if (value === UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION) {
      setIsPlannedEnrolmentCalculation(checked);
      setIsRangeAllocation(!checked);
      setUpdatedSchoolParams((prev: UpdatedSchoolParams) => ({
        ...prev,
        unitCoordinationAllocationMethod: checked
          ? UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION
          : UC_ALLOCATION_METHOD.RANGE_ALLOCATION,
      }));
    }
  };

  useEffect(() => {
    if (ucData.unitCoordinationAllocationMethod === UC_ALLOCATION_METHOD.RANGE_ALLOCATION) {
      setIsRangeAllocation(true);
      setIsPlannedEnrolmentCalculation(false);
    }
    if (ucData.unitCoordinationAllocationMethod === UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION) {
      setIsPlannedEnrolmentCalculation(true);
      setIsRangeAllocation(false);
    }
  }, [ucData.unitCoordinationAllocationMethod]);

  return (
    <>
      <h3 className="text-sh3 font-bold text-primary-black-90">Unit coordination</h3>
      <p className="mb-10 mt-3 text-b2 text-primary-black-90">
        Please select one of the methods if you wish to edit fields.
      </p>
      <div className="flex items-center space-x-3">
        <Checkbox
          id={UC_ALLOCATION_METHOD.RANGE_ALLOCATION}
          data-key="unitCoordinationAllocationMethod"
          className="h-6 w-6 border-secondary-grey-04"
          checked={isRangeAllocation}
          onCheckedChange={(checked) => handleOnCheckedChange(!!checked, UC_ALLOCATION_METHOD.RANGE_ALLOCATION)}
        />
        <label htmlFor={UC_ALLOCATION_METHOD.RANGE_ALLOCATION} className="text-b2 font-bold">
          Using the range allocation method
        </label>
      </div>
      <dl className="mt-3 grid grid-cols-[max-content_1fr] items-center gap-x-5 gap-y-3 text-b3 text-secondary-grey-02 [grid-auto-rows:55px]">
        <dt>Small units are up to</dt>
        <dd className="flex items-center gap-2">
          <NumberInput
            className="h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationSmallUnitsStudents}
            data-category="uc"
            data-key="unitCoordinationSmallUnitsStudents"
            onChange={handleOnChange}
            disabled={!isRangeAllocation}
          />
          students, allowing for
          <NumberInput
            className="ml-5 h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationSmallUnitsHours}
            data-category="uc"
            data-key="unitCoordinationSmallUnitsHours"
            onChange={handleOnChange}
            disabled={!isRangeAllocation}
            decimalScale={2}
          />
          hours per unit
        </dd>

        <dt>Medium units are up to</dt>
        <dd className="flex items-center gap-2">
          <NumberInput
            className="h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationMediumUnitsStudents}
            data-category="uc"
            data-key="unitCoordinationMediumUnitsStudents"
            onChange={handleOnChange}
            disabled={!isRangeAllocation}
          />
          students, allowing for
          <NumberInput
            className="ml-5 h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationMediumUnitsHours}
            data-category="uc"
            data-key="unitCoordinationMediumUnitsHours"
            onChange={handleOnChange}
            disabled={!isRangeAllocation}
            decimalScale={2}
          />
          hours per unit
        </dd>

        <dt>Large units are over</dt>
        <dd className="flex items-center gap-2">
          <NumberInput
            className="h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={largeUnitsStudents}
            data-category="uc"
            data-key="unitCoordinationMediumUnitsStudents"
            disabled
          />
          students, allowing for
          <NumberInput
            className="ml-5 h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationLargeUnitsHours}
            data-category="uc"
            data-key="unitCoordinationLargeUnitsHours"
            onChange={handleOnChange}
            disabled={!isRangeAllocation}
            decimalScale={2}
          />
          hours per unit
        </dd>
      </dl>

      <h1 className="py-10 text-sh3 font-bold">OR</h1>

      <div className="flex items-center space-x-3">
        <Checkbox
          id={UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION}
          data-key="unitCoordinationAllocationMethod"
          value={UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION}
          className="h-6 w-6 border-secondary-grey-04"
          checked={isPlannedEnrolmentCalculation}
          onCheckedChange={(checked) =>
            handleOnCheckedChange(!!checked, UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION)
          }
        />
        <label htmlFor={UC_ALLOCATION_METHOD.PLANNED_ENROLMENT_CALCULATION} className="text-b2 font-bold">
          Using the planned enrolment calculation method
        </label>
      </div>

      <dl className="mt-3 grid grid-cols-[max-content_1fr] items-center gap-x-5 gap-y-3 text-b3 text-secondary-grey-02 [grid-auto-rows:55px]">
        <dt>Fixed at a total of up to</dt>
        <dd className="flex items-center gap-2">
          <NumberInput
            className="h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationFixedTotalHours}
            data-category="uc"
            data-key="unitCoordinationFixedTotalHours"
            onChange={handleOnChange}
            disabled={!isPlannedEnrolmentCalculation}
            decimalScale={2}
          />
          hours plus up to
          <NumberInput
            className="ml-5 h-[30px] w-[76px] border-secondary-grey-04 text-b2"
            min={0}
            value={ucData.unitCoordinationFixedTotalMinutesStudent}
            data-category="uc"
            data-key="unitCoordinationFixedTotalMinutesStudent"
            onChange={handleOnChange}
            disabled={!isPlannedEnrolmentCalculation}
            decimalScale={2}
          />
          minutes per student
        </dd>
      </dl>
    </>
  );
};
